'use client';

import { PERMISSIONS } from '@terra/shared-authorization';
import { AI_CAPACITY_LICENSE_ENABLED_FLAG } from '@terra/shared-contracts-feature-flags';
import { FullRescanControlCard, toasterApi } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useApplicationScanEffort } from '../../../hooks/api/useApplicationScanEffort';
import {
  useLatestApplicationFullRescan,
  useStartApplicationFullRescan,
} from '../../../hooks/api/useApplicationFullRescan';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { usePermissions } from '../../../hooks/usePermissions';

type FullRescanControlProps = {
  applicationId: string;
  isReadOnly?: boolean;
};

export function FullRescanControl({
  applicationId,
  isReadOnly,
}: FullRescanControlProps) {
  const t = useTranslations('designSystem.scanEffort.fullRescan');
  const { isFeatureFlagEnabled } = useFeatureFlags(
    AI_CAPACITY_LICENSE_ENABLED_FLAG,
  );
  const { hasPermission } = usePermissions();
  const canRescan =
    hasPermission(PERMISSIONS.EXECUTE_FULL_RESCAN.name) && !isReadOnly;
  const { data: effort } = useApplicationScanEffort({
    applicationId,
    enabled: isFeatureFlagEnabled,
  });
  const { data } = useLatestApplicationFullRescan({
    applicationId,
    enabled: isFeatureFlagEnabled,
  });
  const startMutation = useStartApplicationFullRescan({ applicationId });

  if (!isFeatureFlagEnabled || !effort?.enabled) {
    return null;
  }

  const handleStart = async (): Promise<boolean> => {
    try {
      await startMutation.mutateAsync();
      toasterApi.success({ title: t('started') });

      return true;
    } catch (error: unknown) {
      toasterApi.error({
        title: t('startFailed'),
        description: error instanceof Error ? error.message : undefined,
      });

      return false;
    }
  };

  return (
    <FullRescanControlCard
      effortLevel={effort.effective.level}
      isActive={data?.run?.active === true}
      isStarting={startMutation.isPending}
      canRescan={canRescan}
      onStart={handleStart}
    />
  );
}
